import Grid from "@mui/material/Grid2";
import { intl } from "LocaleWrapper";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { FormGroup, Textarea } from "@cofano/cck";
import RoundingRuleSelect from "../select/RoundingRuleSelect";
import RoundingUnitSelect from "../select/RoundingUnitSelect";
import RoundingScopeSelect from "../select/RoundingScopeSelect";
import PeriodRegimeSelect from "../select/PeriodRegimeSelect";
import DemurrageCalculationUnitSelect from "../select/DemurrageCalculationUnitSelect";
import { DemurrageCalculationRoundingRule, DemurrageCalculationUnit, DemurrageCalculationPeriodRegime, DemurrageCalculationRoundingUnit, DemurrageCalculationRoundingScope, CallType } from "__generated__/graphql";
import { Fragment, ReactNode } from "react";
import NumberInput from "components/cck/NumberInput";
import FormField from "components/cck/FormField";
import type { CalculationMethodFormByTypeProps } from "@schemas/calculationmethods/schema";
import CallTypeSelect from "components/selects/CallTypeSelect";

type FormValues = {
	agreements: {
		calculationMethod: {
			demurrageCalculationUnit?: DemurrageCalculationUnit;
			price?: number;
			laytimePeriodRegime?: DemurrageCalculationPeriodRegime;
			laytimeRoundingRule?: DemurrageCalculationRoundingRule;
			laytimeRoundingUnit?: DemurrageCalculationRoundingUnit;
			laytimeRoundingScope?: DemurrageCalculationRoundingScope;
			laytimeAllowed?: number;
			description?: string;
			defaultCargoActionTypes?: CallType[];
		}
	}[];
}

type FormField = {
	id: string;
	render: () => ReactNode;
}

const DemurrageCalculationCalculationMethodForm = ({ index, currency, hiddenFields }: CalculationMethodFormByTypeProps) => {
	const { control, setValue, formState: { errors } } = useFormContext<FormValues>();

	const demurrageCalculationUnit = useWatch({ control, name: `agreements.${index}.calculationMethod.demurrageCalculationUnit` });
	const laytimeRoundingRule = useWatch({ control, name: `agreements.${index}.calculationMethod.laytimeRoundingRule` });
	const showLaytimeRounding = laytimeRoundingRule != null && laytimeRoundingRule !== DemurrageCalculationRoundingRule.None;

	const LaytimeAllowedAndPeriodRegimeFields: FormField[] = [
		{
			id: "calculationMethod.laytimePeriodRegime",
			render: () => (
				<Grid size={6}>
					<FormField errors={errors} name={`agreements.${index}.calculationMethod.laytimePeriodRegime`}>
						<FormGroup label={intl.formatMessage({ id: "PERIOD_REGIME" })}>
							<Controller
								control={control}
								render={({ field }) => (
									<PeriodRegimeSelect {...field} />
								)}
								name={`agreements.${index}.calculationMethod.laytimePeriodRegime`} />
						</FormGroup>
					</FormField>
				</Grid>
			),
		},
		{
			id: "calculationMethod.laytimeAllowed",
			render: () => (
				<Grid size={6}>
					<FormField errors={errors} name={`agreements.${index}.calculationMethod.laytimeAllowed`}>
						<FormGroup label={intl.formatMessage({ id: "LAYTIME_ALLOWED" })} overflow="hidden" required>
							<Controller
								rules={{ required: true }}
								name={`agreements.${index}.calculationMethod.laytimeAllowed`}
								control={control}
								render={({ field }) => <NumberInput {...field} addonAfter={intl.formatMessage({ id: "HOURS" })} />} />
						</FormGroup>
					</FormField>
				</Grid>
			),
		},
	];

	const LaytimeRoundingFields: FormField[] = [
		{
			id: "calculationMethod.laytimeRoundingRule",
			render: () => (
				<Grid size={4}>
					<FormField errors={errors} name={`agreements.${index}.calculationMethod.laytimeRoundingRule`}>
						<FormGroup label={intl.formatMessage({ id: "ROUNDING_RULE" })}>
							<Controller
								control={control}
								render={({ field }) => (
									<RoundingRuleSelect
										{...field}
										onChange={(e) => {
											field.onChange(e);
											if (e === DemurrageCalculationRoundingRule.None) {
												// clear other rounding fields
												setValue(`agreements.${index}.calculationMethod.laytimeRoundingUnit`, undefined);
												setValue(`agreements.${index}.calculationMethod.laytimeRoundingScope`, undefined);
											}
										}}
									/>
								)}
								name={`agreements.${index}.calculationMethod.laytimeRoundingRule`} />
						</FormGroup>
					</FormField>
				</Grid>
			),
		},
		{
			id: "calculationMethod.laytimeRoundingUnit",
			render: () => showLaytimeRounding && (
				<Grid size={4}>
					<FormField errors={errors} name={`agreements.${index}.calculationMethod.laytimeRoundingUnit`}>
						<FormGroup label={intl.formatMessage({ id: "ROUNDING_UNIT" })} required>
							<Controller
								control={control}
								rules={{ required: true }}
								render={({ field }) => (
									<RoundingUnitSelect {...field} />
								)}
								name={`agreements.${index}.calculationMethod.laytimeRoundingUnit`} />
						</FormGroup>
					</FormField>
				</Grid>
			),
		},
		{
			id: "calculationMethod.laytimeRoundingScope",
			render: () => showLaytimeRounding && (
				<Grid size={4}>
					<FormField errors={errors} name={`agreements.${index}.calculationMethod.laytimeRoundingScope`}>
						<FormGroup label={intl.formatMessage({ id: "ROUNDING_SCOPE" })} required>
							<Controller
								control={control}
								rules={{ required: true }}
								render={({ field }) => (
									<RoundingScopeSelect {...field} />
								)}
								name={`agreements.${index}.calculationMethod.laytimeRoundingScope`} />
						</FormGroup>
					</FormField>
				</Grid>
			),
		},
	];

	const DemurrageCalculationCalculationMethodFormFields: FormField[] = [
		{
			id: "calculationMethod.demurrageCalculationUnit",
			render: () => (
				<Grid size={3}>
					<FormField errors={errors} name={`agreements.${index}.calculationMethod.demurrageCalculationUnit`}>
						<FormGroup label={intl.formatMessage({ id: "DEMURRAGE_CALCULATION_UNIT" })} required>
							<Controller
								rules={{ required: true }}
								control={control}
								render={({ field }) => (
									<DemurrageCalculationUnitSelect {...field} />
								)}
								name={`agreements.${index}.calculationMethod.demurrageCalculationUnit`} />
						</FormGroup>
					</FormField>
				</Grid>
			),
		},
		{
			id: "calculationMethod.price",
			render: () => demurrageCalculationUnit && (
				<Grid size={3}>
					<FormField errors={errors} name={`agreements.${index}.calculationMethod.price`}>
						<FormGroup
							label={intl.formatMessage({ id: `PRICE_PER_${demurrageCalculationUnit}` })}
							overflow="hidden"
							required>
							<Controller
								name={`agreements.${index}.calculationMethod.price`}
								control={control}
								rules={{ required: true }}
								render={({ field }) => (
									<NumberInput
										{...field}
										addonBefore={currency.symbol} />
								)} />
						</FormGroup>
					</FormField>
				</Grid>
			),
		},
		{
			id: "calculationMethod.defaultCargoActionTypes",
			render: () => {
				const callTypeOrder = [ CallType.NitrogenPurging, CallType.Loading, CallType.Discharging, CallType.Cleaning ];
				return <Grid size={6}>
					<FormGroup
						label={intl.formatMessage({ id: "DEMURRAGE_CALCULATION_DEFAULT_CARGO_ACTION_TYPES" })}
						overflow="hidden">
						<FormField
							errors={errors}
							name={`agreements.${index}.calculationMethod.defaultCargoActionTypes`}>
							<Controller
								name={`agreements.${index}.calculationMethod.defaultCargoActionTypes`}
								control={control}
								render={({ field }) => (
									<CallTypeSelect {...field}
										value={field.value?.sort((a, b) => callTypeOrder.indexOf(a) - callTypeOrder.indexOf(b)) || []}
										additionalOptions={[ CallType.NitrogenPurging, CallType.Cleaning ]}
										multiple />
								)} />
						</FormField>
					</FormGroup>
				</Grid>;
			},
		},
		{
			id: "calculationMethod.laytimeAllowedAndPeriodRegime",
			render: () => (
				<Grid container size={6} spacing={2}>
					{LaytimeAllowedAndPeriodRegimeFields.map(x => x.render())}
				</Grid>
			),
		},
		{
			id: "calculationMethod.laytimeRounding",
			render: () => (
				<Grid container size={6} spacing={2}>
					{LaytimeRoundingFields.map(x => x.render())}
				</Grid>
			),
		},
		{
			id: "calculationMethod.description",
			render: () => (
				<Grid container spacing={2} size={12}>
					<Grid size={6}>
						<FormField errors={errors} name={`agreements.${index}.calculationMethod.description`}>
							<FormGroup label={intl.formatMessage({ id: "DEMURRAGE_CALCULATION_INSTRUCTIONS" })} overflow="hidden">
								<Controller
									name={`agreements.${index}.calculationMethod.description`}
									control={control}
									render={({ field }) => (
										<Textarea
											{...field} />
									)} />
							</FormGroup>
						</FormField>
					</Grid>
				</Grid>
			),
		},
	];

	return (
		<Grid container size={12} spacing={2}>
			{DemurrageCalculationCalculationMethodFormFields
				.filter(x => !hiddenFields?.includes(x.id))
				.map(x => (
					<Fragment key={x.id}>
						{x.render()}
					</Fragment>
				))
			}
		</Grid>
	);
};

export default DemurrageCalculationCalculationMethodForm;
